import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface ContactFormProps {
  businessName: string;
  businessEmail?: string;
  businessId?: string;
  primaryColor?: string;
  textColor?: string;
  className?: string;
  buttonStyle?: React.CSSProperties;
  buttonClassName?: string;
  labelClassName?: string;
  showHeader?: boolean;
}

const ContactForm: React.FC<ContactFormProps> = ({ 
  businessName, 
  businessEmail,
  businessId,
  primaryColor = '#3B82F6',
  textColor = '#1F2937',
  className = '',
  buttonStyle,
  buttonClassName,
  labelClassName = '',
  showHeader = true
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [verificationError, setVerificationError] = useState<string>('');
  const [isRobotChecked, setIsRobotChecked] = useState(false);
  const [showMathModal, setShowMathModal] = useState(false);
  const [mathQuestion, setMathQuestion] = useState<{ a: number; b: number; answer: number } | null>(null);
  const [mathAnswer, setMathAnswer] = useState<string>('');
  const [isVerified, setIsVerified] = useState(false);

  // Generate a new math question
  const generateMathQuestion = () => {
    const a = Math.floor(Math.random() * 20) + 1; // 1-20
    const b = Math.floor(Math.random() * 20) + 1; // 1-20
    setMathQuestion({ a, b, answer: a + b });
    setMathAnswer('');
  };

  // When checkbox is clicked, show math modal
  useEffect(() => {
    if (isRobotChecked && !isVerified) {
      generateMathQuestion();
      setShowMathModal(true);
    }
  }, [isRobotChecked]);

  // Reset verification when checkbox is unchecked
  useEffect(() => {
    if (!isRobotChecked) {
      setIsVerified(false);
      setMathAnswer('');
      setMathQuestion(null);
    }
  }, [isRobotChecked]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMathSubmit = () => {
    if (!mathQuestion) return;
    
    const userAnswer = parseInt(mathAnswer);
    if (userAnswer === mathQuestion.answer) {
      setIsVerified(true);
      setShowMathModal(false);
      setVerificationError('');
    } else {
      setVerificationError('Incorrect answer. Please try again.');
      generateMathQuestion();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerificationError('');
    
    // Validate verification
    if (!isVerified) {
      setVerificationError('Please verify that you are not a robot');
      return;
    }

    if (!businessId) {
      setSubmitStatus('error');
      setVerificationError('Business ID is required');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await apiService.submitContactForm(businessId, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        message: formData.message,
        recaptchaToken: '' // Not needed anymore, but keeping for API compatibility
      });
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setIsRobotChecked(false);
      setIsVerified(false);
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error: any) {
      setSubmitStatus('error');
      if (error.response?.data?.message) {
        setVerificationError(error.response.data.message);
      } else {
        setVerificationError('Failed to send message. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {showHeader && (
        <h3 
          className="text-2xl font-bold mb-6"
          style={{ color: textColor }}
        >
          Contact Us
        </h3>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label 
            htmlFor="name" 
            className={`block text-sm mb-2 ${labelClassName}`}
            style={{ color: textColor }}
          >
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors"
            style={{ 
              backgroundColor: '#FFFFFF',
              color: textColor,
              borderColor: '#D1D5DB',
              outline: 'none'
            }}
            placeholder="Your full name"
            onFocus={(e) => {
              e.target.style.borderColor = primaryColor;
              e.target.style.boxShadow = `0 0 0 2px ${primaryColor}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#D1D5DB';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div>
          <label 
            htmlFor="email" 
            className={`block text-sm mb-2 ${labelClassName}`}
            style={{ color: textColor }}
          >
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors"
            style={{ 
              backgroundColor: '#FFFFFF',
              color: textColor,
              borderColor: '#D1D5DB',
              outline: 'none'
            }}
            placeholder="your.email@example.com"
            onFocus={(e) => {
              e.target.style.borderColor = primaryColor;
              e.target.style.boxShadow = `0 0 0 2px ${primaryColor}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#D1D5DB';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div>
          <label 
            htmlFor="phone" 
            className={`block text-sm mb-2 ${labelClassName}`}
            style={{ color: textColor }}
          >
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors"
            style={{ 
              backgroundColor: '#FFFFFF',
              color: textColor,
              borderColor: '#D1D5DB',
              outline: 'none'
            }}
            placeholder="(555) 123-4567"
            onFocus={(e) => {
              e.target.style.borderColor = primaryColor;
              e.target.style.boxShadow = `0 0 0 2px ${primaryColor}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#D1D5DB';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div>
          <label 
            htmlFor="message" 
            className={`block text-sm mb-2 ${labelClassName}`}
            style={{ color: textColor }}
          >
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            required
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors resize-none"
            style={{ 
              backgroundColor: '#FFFFFF',
              color: textColor,
              borderColor: '#D1D5DB',
              outline: 'none'
            }}
            placeholder="Tell us how we can help you..."
            onFocus={(e) => {
              e.target.style.borderColor = primaryColor;
              e.target.style.boxShadow = `0 0 0 2px ${primaryColor}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#D1D5DB';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <div className="flex items-start">
          <input
            type="checkbox"
            id="notRobot"
            checked={isRobotChecked}
            onChange={(e) => setIsRobotChecked(e.target.checked)}
            className="mt-1 mr-2 w-4 h-4 cursor-pointer"
            style={{ accentColor: primaryColor }}
          />
          <label 
            htmlFor="notRobot" 
            className="text-sm cursor-pointer"
            style={{ color: textColor }}
          >
            I am not a robot
          </label>
        </div>
        {verificationError && (
          <p className="mt-1 text-sm text-red-600">{verificationError}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-3 px-6 font-medium transition-opacity disabled:opacity-50 disabled:cursor-not-allowed ${buttonClassName || 'rounded-lg'}`}
          style={{
            ...buttonStyle,
            color: buttonStyle?.color || '#FFFFFF'
          }}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>

        {submitStatus === 'success' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">
              Thank you! Your message has been sent successfully. We'll get back to you soon.
            </p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">
              Sorry, there was an error sending your message. Please try again.
            </p>
          </div>
        )}
      </form>

      {/* Math Challenge Modal */}
      {showMathModal && mathQuestion && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowMathModal(false);
            setIsRobotChecked(false);
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            style={{ color: textColor }}
          >
            <h3 className="text-xl font-bold mb-4" style={{ color: textColor }}>
              Verification Challenge
            </h3>
            <p className="mb-4 text-gray-600">
              Please solve this simple math problem to verify you are human:
            </p>
            <div className="mb-4">
              <p className="text-2xl font-bold text-center mb-4" style={{ color: primaryColor }}>
                {mathQuestion.a} + {mathQuestion.b} = ?
              </p>
              <input
                type="number"
                value={mathAnswer}
                onChange={(e) => setMathAnswer(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleMathSubmit();
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                style={{ 
                  borderColor: '#D1D5DB',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryColor;
                  e.target.style.boxShadow = `0 0 0 2px ${primaryColor}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#D1D5DB';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="Enter your answer"
                autoFocus
              />
            </div>
            {verificationError && (
              <p className="mb-4 text-sm text-red-600">{verificationError}</p>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleMathSubmit}
                className="flex-1 py-2 px-4 rounded-lg font-medium transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: primaryColor,
                  color: '#FFFFFF'
                }}
              >
                Verify
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowMathModal(false);
                  setIsRobotChecked(false);
                  setVerificationError('');
                }}
                className="flex-1 py-2 px-4 rounded-lg font-medium border transition-colors hover:bg-gray-50"
                style={{
                  borderColor: '#D1D5DB',
                  color: textColor
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactForm; 